import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot} from '@angular/router';
import {Store} from "@ngrx/store";
import * as fromStore from "../../../store";
import {filter, first, switchMap} from "rxjs/operators";
import {Config} from "../../../config/config";
import {combineLatest} from "rxjs";
import {UserSubscription} from "../../../user/user-subscription";
import {User} from "../../../user/user";
import {PluginConnectService} from "../../../shared/services/plugin-connect/plugin-connect.service";
import {Utils} from "../../../shared/utils/utils";

const SKIP_ONBOARDING_ANSWERS = ['plugins', 'Other - other', 'Other - plugins', 'skip', 'booster'];

@Injectable({
  providedIn: 'root'
})
export class SetupGuard implements CanActivate {

  constructor(
    private _store: Store<fromStore.DbStoreState>,
    private _router: Router,
    private readonly _pluginConnectService: PluginConnectService,

  ) {}

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> | boolean {
    return new Promise<boolean>((resolve: Function, reject: Function): void => {
      this._store.select(fromStore.getUserLoaded)
        .pipe(
          first(loaded => loaded),
          switchMap(() => combineLatest(
            this._store.select(fromStore.getUser),
            this._store.select(fromStore.getUserLocalStorage),
            this._store.select(fromStore.getUserSubscription),
            this._store.select(fromStore.getWebsitesLoaded)
              .pipe(
                filter((websitesLoaded: boolean) => websitesLoaded),
                switchMap(() =>
                  this._store.select(fromStore.getWebsites)
                ),
                first()
              )
          )),
          first()
        )
        .subscribe(([user, userLocalStorage, userSubscription, websites]) => {
          const demoUser = Utils.isDemoUser(userSubscription, user);
          const queryParams: any = next.queryParams;
          if (userSubscription) {
            return this._redirectToWebsites(next.queryParams, reject);
          }
          if (websites && websites.length && this._setupAiRecreateCondition(user, userLocalStorage)) {
            return resolve(true);
          }

         if (user.websiteCreated || user.websiteConnected) {
            localStorage.removeItem("dbDemoCampaign");
            localStorage.removeItem("dbDemoCampaignInfo");
            localStorage.removeItem("dbCouponFromEmail");
            return this._redirectToWebsites(next.queryParams, reject);
          }

          /** skip onboarding **/
          if (user && (user && user.signUpFor && user.signUpFor.Action
            && SKIP_ONBOARDING_ANSWERS.includes(user.signUpFor.Action.answer))) {
            return this._redirectToWebsites(next.queryParams, reject);
          }

          resolve(true);
        });
    });
  }

  private _trialSubscriptionCondition(userSubscription: UserSubscription): boolean {
    return !!(userSubscription && (userSubscription.subscriptionId === Config.defaultStarterPlanId
      || userSubscription.freeBaseClient
      || userSubscription.trialClient
      || userSubscription.trialOptimizerClient));
  }

  private _setupAiRecreateCondition(user: User, userLocalStorage: any): boolean {
    return !!(user && user.signUpFor && user.signUpFor.LastChosenOption && user.signUpFor.LastChosenOption.answer
      && ['ai-demo-section', 'ai-demo-section-after-signup', 'ai-demo-template', 'ai-demo-template-after-signup'].includes(user.signUpFor.LastChosenOption.answer)
      && (userLocalStorage.setupAiRecreation || userLocalStorage.setupAiCreation));
  }

  private _redirectToWebsites(queryParams: any, reject: Function): void {
    this._router.navigate(['/websites'], {queryParams});
    reject();
  }

  private hasReturnUrl(queryParams): boolean {
    if (this._pluginConnectService.pluginData && this._pluginConnectService.pluginData.returnUrl) {
      return !!this._pluginConnectService.pluginData.returnUrl;
    }
    else if (localStorage.getItem('pluginData')) {
      const pluginData = JSON.parse(localStorage.getItem('pluginData'));
      return !!pluginData.returnUrl;
    }
    else if (queryParams && queryParams.return_url) {
      return !!queryParams.return_url;
    }
    return false;
  }
}
